import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import { createSelector } from 'reselect';

export const COMPANY_TIMESHEETS_SLICE = 'companyTimesheets' as const;

export interface CompanyTimesheetsSettingsResponse {
  locations: { [id: number]: string };
  settings: {
    holiday_pay_enabled: boolean;
    double_overtimes: boolean;
    spread_of_hours_enabled: boolean;
    blue_laws: boolean;
    break_penalty_enabled: boolean;
  };
}

export interface CompanyTimesheetsState {
  locationNamesMap: { [id: number]: string };
  settings: {
    holiday_pay_enabled: boolean;
    double_overtimes: boolean;
    spread_of_hours_enabled: boolean;
    blue_laws: boolean;
    break_penalty_enabled: boolean;
  } | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CompanyTimesheetsState = {
  locationNamesMap: {},
  settings: null,
  status: 'idle',
  error: null,
};

export const fetchCompanyTimesheetsSettings =
  createAsyncThunk<CompanyTimesheetsSettingsResponse>(
    `${COMPANY_TIMESHEETS_SLICE}/fetchSettings`,
    async () =>
      (await fetchJSON('/timesheets/company/settings', {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })) as CompanyTimesheetsSettingsResponse
  );

// Slice
const companyTimesheetsSlice = createSlice({
  name: COMPANY_TIMESHEETS_SLICE,
  initialState,
  reducers: {
    setNavigationLoading: (state, action: PayloadAction<boolean>) => {
      state.status = action.payload ? 'loading' : 'idle';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCompanyTimesheetsSettings.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCompanyTimesheetsSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';

        if (action.payload) {
          state.locationNamesMap = action.payload.locations;
          state.settings = action.payload.settings;
        }
      })
      .addCase(fetchCompanyTimesheetsSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.error.message || 'Failed to fetch company timesheets settings';
      });
  },
});

export const { setNavigationLoading } = companyTimesheetsSlice.actions;
export const { reducer } = companyTimesheetsSlice;

// Selectors
export const selectCompanyTimesheetsState = (state: RootState) =>
  state.get(COMPANY_TIMESHEETS_SLICE);

export const selectCompanyTimesheetsStatus = (state: RootState) =>
  state.get(COMPANY_TIMESHEETS_SLICE).status;

export const selectCompanyTimesheetsSettings = (state: RootState) =>
  state.get(COMPANY_TIMESHEETS_SLICE).settings;

export const selectLocationNamesMap = (state: RootState) =>
  state.get(COMPANY_TIMESHEETS_SLICE).locationNamesMap;

export const selectCompanyTimesheetLocationSettings = createSelector(
  [selectCompanyTimesheetsSettings],
  settings => {
    if (!settings) {
      return {
        hasHolidayPay: false,
        hasDoubleOT: false,
        hasSOH: false,
        hasBlueLaws: false,
        hasBreakPenalty: false,
      };
    }

    return {
      hasHolidayPay: settings.holiday_pay_enabled,
      hasDoubleOT: settings.double_overtimes,
      hasSOH: settings.spread_of_hours_enabled,
      hasBlueLaws: settings.blue_laws,
      hasBreakPenalty: settings.break_penalty_enabled,
    };
  }
);
